'use client';

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { z } from 'zod';
import { useUniversalChat } from '@/components/chat/universal-chat-context';
import { ConversationDrawer } from '@/components/conversations/ConversationDrawer';
import { useConversationThread } from '@/components/conversations/useConversationThread';
import InlineTweet from '@/domains/timeline/components/InlineTweet';
import { ManualRetweet } from '@/domains/timeline/components/ManualRetweet';
import { useTimelineConversationSurface } from '@/domains/timeline/components/TimelineConversationSurfaceContext';
import { useAuth } from '@/hooks/useAuth';
import { detectManualRetweet } from '@/lib/detect-manual-retweet';
import { toastError } from '@/lib/errors/toast-error';
import type { LinkMetadata } from '@/lib/fetch-metadata';
import { useBetaFeatures } from '@/lib/hooks/api/settings-hooks';
import { useMuteActions, useTweetActions } from '@/lib/hooks/api/social-actions';
import { isTestRuntime } from '@/lib/runtime-flags';
import { trpc } from '@/lib/trpc/client';
import { type ParseableTweet, parseTweetText, type TweetMedia, type TweetUrl } from '@/lib/tweet-parser';
import { toLinkMetadataRecord } from '@/lib/url-metadata/normalize';
import { toVoidPromise } from '@/lib/utils/async-handlers';
import {
  type BrowserTimeoutHandle,
  clearBrowserTimeout,
  reloadBrowserLocation,
  setBrowserTimeout,
} from '@/lib/utils/browser-window';
import { buildUserFacingErrorMessage, logNormalizedError, normalizeError } from '@/lib/utils/error-messages';
import type { TweetEntities, TweetMetrics, Tweet as TweetType } from '@/types/tweet';
import type { MediaMetadataEntry } from '@/types/tweet/media';
import { RetweetWrapper } from './RetweetWrapper';
import { TweetContent } from './TweetContent';
import { TweetPinnedBadge } from './TweetPinnedBadge';

interface TweetHandlers {
  copyLink: () => void;
  reply: () => void;
  retweet: () => void;
  share: () => void;
  toggleBookmark: () => void;
  toggleLike: () => void;
  toggleMute: () => void;
}

export interface TweetProperties {
  className?: string;
  defaultExpandText?: boolean;
  disableConversationTrigger?: boolean;
  disableHover?: boolean;
  disableLinkPreviews?: boolean;
  enableActions?: boolean;
  highlightCardUrl?: string;
  /**
   * Media render mode controls whether tweets use the full interactive viewer (with video playback and modals)
   * or the lightweight static markup version that avoids layout thrash in constrained surfaces (e.g. hover cards).
   */
  mediaRenderMode?: 'interactive' | 'static';
  tweet: TweetType & {
    retweetedTweet?: TweetType | undefined;
  };
  variant?: 'default' | 'compact';
}

const coerceMetadataRecord = (value: unknown): Record<string, LinkMetadata> | undefined =>
  toLinkMetadataRecord(value) ?? undefined;

const buildParseableTweet = ({
  text,
  mediaItems,
  metadata,
}: {
  text: string;
  mediaItems?: TweetMedia[] | undefined;
  metadata?: Record<string, LinkMetadata> | undefined;
}): ParseableTweet => {
  const base: ParseableTweet = { text };
  if (mediaItems && mediaItems.length > 0) {
    base.mediaItems = mediaItems;
  }
  if (metadata) {
    base.metadata = metadata;
  }
  return base;
};

type ManualRetweetEntities = {
  urls?: Array<{
    url: string;
    expandedUrl: string;
    displayUrl: string;
    title: string | undefined;
    description: string | undefined;
  }>;
  mentions?: Array<{ username: string; id?: string; start?: number; end?: number }>;
  hashtags?: Array<{ tag: string; start?: number; end?: number }>;
};

const readSnakeCaseString = (record: Record<string, unknown>, key: string): string | undefined => {
  const value = Reflect.get(record, key);
  return typeof value === 'string' ? value : undefined;
};

const ManualRetweetUrlSchema = z
  .object({
    url: z.string(),
    expandedUrl: z.string().optional(),
    displayUrl: z.string().optional(),
    title: z.string().optional(),
    description: z.string().optional(),
  })
  .loose();

const ManualRetweetMentionSchema = z
  .object({
    username: z.string(),
    id: z.string().optional(),
    start: z.number().optional(),
    end: z.number().optional(),
  })
  .loose();

const ManualRetweetHashtagSchema = z
  .object({
    tag: z.string(),
    start: z.number().optional(),
    end: z.number().optional(),
  })
  .loose();

const normalizeManualRetweetUrls = (value: unknown): ManualRetweetEntities['urls'] => {
  const parsed = z.array(ManualRetweetUrlSchema).safeParse(value);
  if (parsed.success && parsed.data.length > 0) {
    return parsed.data.map((candidate) => {
      const candidateRecord = candidate as Record<string, unknown>;
      const fallbackExpandedUrl = readSnakeCaseString(candidateRecord, 'expanded_url');
      const fallbackDisplayUrl = readSnakeCaseString(candidateRecord, 'display_url');
      return {
        url: candidate.url,
        expandedUrl: candidate.expandedUrl ?? fallbackExpandedUrl ?? candidate.url,
        displayUrl: candidate.displayUrl ?? fallbackDisplayUrl ?? candidate.url,
        title: candidate.title,
        description: candidate.description,
      };
    });
  }
  return;
};

const normalizeManualRetweetMentions = (value: unknown): ManualRetweetEntities['mentions'] => {
  const parsed = z.array(ManualRetweetMentionSchema).safeParse(value);
  if (parsed.success && parsed.data.length > 0) {
    return parsed.data.map(({ username, id, start, end }) => {
      const mention: { username: string; id?: string; start?: number; end?: number } = { username };
      if (typeof id === 'string') {
        mention.id = id;
      }
      if (typeof start === 'number') {
        mention.start = start;
      }
      if (typeof end === 'number') {
        mention.end = end;
      }
      return mention;
    });
  }
  return;
};

const normalizeManualRetweetHashtags = (value: unknown): ManualRetweetEntities['hashtags'] => {
  const parsed = z.array(ManualRetweetHashtagSchema).safeParse(value);
  if (parsed.success && parsed.data.length > 0) {
    return parsed.data.map(({ tag, start, end }) => {
      const hashtag: { tag: string; start?: number; end?: number } = { tag };
      if (typeof start === 'number') {
        hashtag.start = start;
      }
      if (typeof end === 'number') {
        hashtag.end = end;
      }
      return hashtag;
    });
  }
  return;
};

const buildManualRetweetEntities = (entities: TweetEntities | null | undefined): ManualRetweetEntities | undefined => {
  if (entities == null) {
    return;
  }
  const urls = normalizeManualRetweetUrls(entities.urls);
  const mentions = normalizeManualRetweetMentions(entities.mentions);
  const hashtags = normalizeManualRetweetHashtags(entities.hashtags);
  if (urls || mentions || hashtags) {
    const next: ManualRetweetEntities = {};
    if (urls) {
      next.urls = urls;
    }
    if (mentions) {
      next.mentions = mentions;
    }
    if (hashtags) {
      next.hashtags = hashtags;
    }
    return next;
  }
  return;
};

function TweetComponentInner({
  tweet,
  enableActions = true,
  disableHover = false,
  className = '',
  disableLinkPreviews = false,
  disableConversationTrigger = false,
  variant = 'default',
  defaultExpandText = false,
  mediaRenderMode = 'interactive',
  highlightCardUrl,
}: TweetProperties) {
  const [conversationOpen, setConversationOpen] = useState(false);
  const [selectedTweetId, setSelectedTweetId] = useState<string | null>(null);
  const conversationSurface = useTimelineConversationSurface();
  const hoverPrefetchTimeoutReference = useRef<BrowserTimeoutHandle>(null);

  useEffect(
    () => () => {
      clearBrowserTimeout(hoverPrefetchTimeoutReference.current);
      hoverPrefetchTimeoutReference.current = null;
    },
    []
  );

  const { thread: conversationThread } = useConversationThread(tweet.id, { enabled: false });
  const conversationCount = useMemo(() => {
    if (!conversationThread) {
      return null;
    }
    return Math.max((conversationThread.conversation.tweetCount ?? 1) - 1, 0);
  }, [conversationThread]);

  const canOpenConversation = !disableConversationTrigger;
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const shouldFetchBetaFeatures = Boolean(user?.id) && !isAdmin;
  const { data: betaFeatures } = useBetaFeatures({ enabled: shouldFetchBetaFeatures });
  const powerUserEnabled = isAdmin || Boolean(betaFeatures?.powerUser);
  const { openChat, registerContext, appendMessages, isEnabled: isChatEnabled } = useUniversalChat();
  const isCompact = variant === 'compact';
  const batchLoadTweetsMutation = trpc.tweets.batchLoadTweets.useMutation({ trpc: { abortOnUnmount: true } });

  const tweetMetadataRecord = useMemo(() => coerceMetadataRecord(tweet.metadata), [tweet.metadata]);

  const {
    metrics,
    isLiked,
    isRetweeted,
    isBookmarked,
    isLoading,
    toggleLike,
    retweet,
    toggleBookmark,
    reply,
    share,
    copyLink,
  } = useTweetActions(
    tweet.id,
    {
      likeCount: tweet.likeCount ?? tweet.metrics?.likeCount ?? 0,
      retweetCount: tweet.retweetCount ?? tweet.metrics?.retweetCount ?? 0,
      replyCount: tweet.replyCount ?? tweet.metrics?.replyCount ?? 0,
      quoteCount: tweet.quoteCount ?? tweet.metrics?.quoteCount ?? 0,
      bookmarkCount: tweet.bookmarkCount ?? tweet.metrics?.bookmarkCount ?? 0,
      impressionCount: tweet.impressionCount ?? tweet.metrics?.impressionCount ?? 0,
    },
    tweet.author?.username || ''
  );

  const { isMuted, toggleMute } = useMuteActions(tweet.author?.id ?? null, tweet.author?.username || '', false);

  const isApiRetweet = tweet.referencedTweets?.some((reference) => reference.type === 'retweeted');
  const retweetedTweet = isApiRetweet ? tweet.retweetedTweet : null;
  const manualRtInfo = detectManualRetweet(tweet.text);
  const isManualRetweet = !isApiRetweet && manualRtInfo.isManualRetweet;
  const quotedReferenceId = tweet.referencedTweets?.find((reference) => reference.type === 'quoted')?.id ?? null;

  const {
    html: initialParsed,
    cardUrls: initialCardUrls,
    inlineTweetUrls: initialInlineTweetUrls,
  } = useMemo(() => {
    const parseableTweet = buildParseableTweet({
      text: tweet.text,
      mediaItems: tweet.mediaItems as TweetMedia[] | undefined,
      metadata: tweetMetadataRecord,
    });
    return parseTweetText(parseableTweet);
  }, [tweet, tweetMetadataRecord]);

  const [parsedText, setParsedText] = useState<string>(initialParsed);
  const [cardUrls, setCardUrls] = useState<TweetUrl[]>(initialCardUrls);
  const [inlineTweetUrls, setInlineTweetUrls] =
    useState<Array<{ url: string; username: string; tweetId: string }>>(initialInlineTweetUrls);

  const noteText = tweet.noteTweet?.text;
  const longFormHint = typeof noteText === 'string' && noteText.length > 0;

  const handleFullTweetLoaded = (next: {
    text: string;
    entities?: TweetEntities;
    mediaItems?: TweetMedia[];
    metadata?: Record<string, unknown>;
    mediaMetadata?: MediaMetadataEntry[];
  }) => {
    const parseable = buildParseableTweet({
      text: next.text,
      mediaItems: next.mediaItems ?? (tweet.mediaItems as TweetMedia[] | undefined),
      metadata: coerceMetadataRecord(next.metadata) ?? tweetMetadataRecord,
    });
    const { html, cardUrls: nextCards, inlineTweetUrls: nextInline } = parseTweetText(parseable);
    setParsedText(html);
    setCardUrls(nextCards);
    setInlineTweetUrls(nextInline);
  };

  const retweetParsed = useMemo(() => {
    if (isApiRetweet && retweetedTweet) {
      const parseableRetweet = buildParseableTweet({
        text: retweetedTweet.text,
        mediaItems: retweetedTweet.mediaItems as TweetMedia[] | undefined,
        metadata: coerceMetadataRecord(retweetedTweet.metadata) ?? undefined,
      });
      return parseTweetText(parseableRetweet);
    }
    return { html: '', cardUrls: [], inlineTweetUrls: [] };
  }, [isApiRetweet, retweetedTweet]);

  const retweetedLongFormHint = Boolean(retweetedTweet?.noteTweet?.text && retweetedTweet.noteTweet.text.length > 0);

  const handlePrefetchMouseEnter = () => {
    if (!(canOpenConversation && conversationSurface?.prefetchConversation)) {
      return;
    }
    if (hoverPrefetchTimeoutReference.current !== null) {
      return;
    }
    const timeoutHandle = setBrowserTimeout(() => {
      conversationSurface.prefetchConversation?.(tweet);
      hoverPrefetchTimeoutReference.current = null;
    }, 500);
    if (timeoutHandle === null) {
      conversationSurface.prefetchConversation?.(tweet);
      return;
    }
    hoverPrefetchTimeoutReference.current = timeoutHandle;
  };

  const handlePrefetchMouseLeave = () => {
    clearBrowserTimeout(hoverPrefetchTimeoutReference.current);
    hoverPrefetchTimeoutReference.current = null;
  };

  const prefetchHandlers = canOpenConversation
    ? {
        onMouseEnter: handlePrefetchMouseEnter,
        onMouseLeave: handlePrefetchMouseLeave,
        onFocusCapture: handlePrefetchMouseEnter,
        onBlurCapture: handlePrefetchMouseLeave,
      }
    : undefined;

  const openConversation = () => {
    if (!canOpenConversation) {
      return;
    }

    if (conversationSurface) {
      conversationSurface.openConversation(tweet);
      return;
    }

    setSelectedTweetId(tweet.id);
    setConversationOpen(true);
  };

  const refreshFromTwitter = useCallback(async () => {
    try {
      // Use full payload refresh to avoid losing required fields during schema hydration.
      await batchLoadTweetsMutation.mutateAsync({ ids: [tweet.id] });
      toast.success('Tweet refreshed from Twitter');
      if (!isTestRuntime) {
        reloadBrowserLocation();
      }
    } catch (error) {
      const normalizedError = normalizeError(error, { fallbackMessage: 'Failed to refresh tweet' });
      // Ensure the toast and debug output stay in sync with other surfaces.
      const errorMessage = buildUserFacingErrorMessage(normalizedError, {
        feature: 'this tweet',
        action: 'refresh',
        fallbackMessage: 'Failed to refresh tweet',
      });
      toastError({ error: errorMessage, title: 'Failed to refresh tweet' });
      logNormalizedError('Tweet.refreshFromTwitter', error, normalizedError, { tweetId: tweet.id });
    }
  }, [batchLoadTweetsMutation, tweet.id]);

  const refreshTweetHandler = useMemo(() => toVoidPromise(refreshFromTwitter), [refreshFromTwitter]);

  const handlers: TweetHandlers = {
    toggleLike,
    retweet,
    toggleBookmark,
    reply,
    share: toVoidPromise(share),
    copyLink: toVoidPromise(copyLink),
    toggleMute,
  };

  const renderConversationDrawer = () =>
    conversationSurface ? null : (
      <ConversationDrawer
        tweetId={selectedTweetId}
        isOpen={conversationOpen}
        onClose={() => {
          setConversationOpen(false);
          setSelectedTweetId(null);
        }}
        renderTweet={(t) => (
          <TweetComponent tweet={t} enableActions={false} disableHover={false} disableConversationTrigger />
        )}
      />
    );

  if (!tweet.author) {
    console.error('Tweet author is undefined for tweet:', tweet.id);
    return (
      <div className="px-3 sm:px-4 py-3 text-muted-foreground">
        <p>Unable to display tweet - author information missing</p>
      </div>
    );
  }

  if (isManualRetweet) {
    const manualRetweetEntities = buildManualRetweetEntities(tweet.entities);
    return (
      <>
        {tweet.isPinned && (
          <div className="px-3 sm:px-4 pt-3">
            <TweetPinnedBadge className="mb-1" />
          </div>
        )}
        <ManualRetweet
          {...(prefetchHandlers ?? {})}
          tweet={{
            id: tweet.id,
            text: tweet.text,
            author: tweet.author,
            ...(manualRetweetEntities ? { entities: manualRetweetEntities } : {}),
          }}
          InlineTweetComponent={InlineTweet}
        />
        {renderConversationDrawer()}
      </>
    );
  }

  if (isApiRetweet && retweetedTweet) {
    if (!retweetedTweet.author) {
      console.error('Retweeted tweet author is undefined for tweet:', retweetedTweet.id);
      return (
        <div className="px-3 sm:px-4 py-3 text-muted-foreground">
          <p>Unable to display retweet - author information missing</p>
        </div>
      );
    }

    const retweetedMetrics: TweetMetrics = {
      likeCount: retweetedTweet.likeCount ?? retweetedTweet.metrics?.likeCount ?? 0,
      retweetCount: retweetedTweet.retweetCount ?? retweetedTweet.metrics?.retweetCount ?? 0,
      replyCount: retweetedTweet.replyCount ?? retweetedTweet.metrics?.replyCount ?? 0,
      quoteCount: retweetedTweet.quoteCount ?? retweetedTweet.metrics?.quoteCount ?? 0,
      bookmarkCount: retweetedTweet.bookmarkCount ?? retweetedTweet.metrics?.bookmarkCount ?? 0,
      impressionCount: retweetedTweet.impressionCount ?? retweetedTweet.metrics?.impressionCount ?? 0,
    };

    const retweetedQuotedId =
      retweetedTweet.referencedTweets?.find((reference) => reference.type === 'quoted')?.id ?? null;
    return (
      <>
        {tweet.isPinned && (
          <div className="px-3 sm:px-4 pt-3">
            <TweetPinnedBadge className="mb-1" />
          </div>
        )}
        <RetweetWrapper retweeter={tweet.author} {...(prefetchHandlers ?? {})}>
          <TweetContent
            tweet={retweetedTweet}
            parsedText={retweetParsed.html}
            cardUrls={retweetParsed.cardUrls}
            inlineTweetUrls={retweetParsed.inlineTweetUrls}
            {...(retweetedQuotedId ? { quotedTweetId: retweetedQuotedId } : {})}
            metrics={retweetedMetrics}
            states={{ isLiked, isRetweeted, isBookmarked, isLoading }}
            handlers={handlers}
            enableActions={enableActions}
            disableHover={disableHover}
            isMuted={isMuted}
            isAdmin={isAdmin}
            className={className}
            onOpenConversation={openConversation}
            {...(powerUserEnabled || isAdmin ? { onRefreshTweet: refreshTweetHandler } : {})}
            longFormHint={retweetedLongFormHint}
            disableLinkPreviews={disableLinkPreviews}
            mediaRenderMode={mediaRenderMode}
            showPinnedBadge={false}
            canOpenConversation={canOpenConversation}
            conversationCount={conversationCount}
            compact={isCompact}
            powerUserEnabled={powerUserEnabled}
            defaultExpandText={defaultExpandText}
            openChat={openChat}
            registerContext={registerContext}
            isChatEnabled={isChatEnabled}
            appendMessages={appendMessages}
            {...(highlightCardUrl ? { highlightCardUrl } : {})}
          />
        </RetweetWrapper>
        {renderConversationDrawer()}
      </>
    );
  }

  return (
    <>
      <TweetContent
        tweet={tweet}
        parsedText={parsedText}
        cardUrls={cardUrls}
        inlineTweetUrls={inlineTweetUrls}
        {...(quotedReferenceId ? { quotedTweetId: quotedReferenceId } : {})}
        metrics={metrics}
        states={{ isLiked, isRetweeted, isBookmarked, isLoading }}
        handlers={handlers}
        enableActions={enableActions}
        disableHover={disableHover}
        isMuted={isMuted}
        isAdmin={isAdmin}
        className={className}
        onOpenConversation={openConversation}
        {...(powerUserEnabled || isAdmin ? { onRefreshTweet: refreshTweetHandler } : {})}
        longFormHint={longFormHint}
        onFullTweetLoaded={handleFullTweetLoaded}
        disableLinkPreviews={disableLinkPreviews}
        mediaRenderMode={mediaRenderMode}
        showPinnedBadge={tweet.isPinned ?? false}
        canOpenConversation={canOpenConversation}
        conversationCount={conversationCount}
        compact={isCompact}
        powerUserEnabled={powerUserEnabled}
        defaultExpandText={defaultExpandText}
        openChat={openChat}
        registerContext={registerContext}
        isChatEnabled={isChatEnabled}
        appendMessages={appendMessages}
        {...(prefetchHandlers ?? {})}
        {...(highlightCardUrl ? { highlightCardUrl } : {})}
      />
      {renderConversationDrawer()}
    </>
  );
}

const TweetComponent = memo(TweetComponentInner);

export default TweetComponent;
